import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button, Input, IconButton } from './ui';
import { X } from 'lucide-react';
import { useSearchStore } from '@/stores/searchStore';
import { useNavigate } from 'react-router-dom';
import SearchFilters from './SearchFilters';
import ViewToggle from './ViewToggle';
import SearchAutocomplete from './SearchAutocomplete';
import type { SearchAutocompleteHandle } from './SearchAutocomplete';
import type { TypeaheadHit } from '../services/algoliaSearch';
import { useMapActions } from '../hooks/useMapActions';

const MobileSearchBar: React.FC = () => {
  const storeQuery = useSearchStore(s => s.query);
  const setQuery = useSearchStore(s => s.setQuery);
  const runSearch = useSearchStore(s => s.runSearch);
  const runSearchWithQuery = useSearchStore(s => s.runSearchWithQuery);
  const resetSearch = useSearchStore(s => s.resetSearch);
  const viewMode = useSearchStore(s => s.viewMode);
  const setViewMode = useSearchStore(s => s.setViewMode);
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState('');
  const [acOpen, setAcOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const acRef = useRef<SearchAutocompleteHandle>(null);

  useEffect(() => {
    setInputValue(storeQuery ?? '');
  }, [storeQuery]);

  const handleClear = () => {
    setAcOpen(false);
    resetSearch();
    navigate('/');
  };

  const doFullSearch = useCallback((q: string) => {
    setAcOpen(false);
    setQuery(q);
    if (typeof runSearchWithQuery === 'function') {
      runSearchWithQuery(q);
    } else {
      queueMicrotask(() => runSearch());
    }
    if (viewMode === 'hero') setViewMode('list');
  }, [setQuery, runSearch, runSearchWithQuery, viewMode, setViewMode]);

  const { focusShopOnMap } = useMapActions();

  const handleSelect = useCallback((hit: TypeaheadHit) => {
    setAcOpen(false);
    setInputValue(hit.name);
    if (viewMode === 'map') {
      // User is browsing the map — fly to the shop there instead of
      // yanking them away to the details page.
      focusShopOnMap({ id: hit.id, name: hit.name });
    } else {
      navigate(`/shop/${hit.id}`);
    }
  }, [navigate, viewMode, focusShopOnMap]);

  const handleSearchAll = useCallback((q: string) => {
    setInputValue(q);
    doFullSearch(q);
  }, [doFullSearch]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    setInputValue(trimmed);
    doFullSearch(trimmed);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (e.target.value.trim().length >= 2) {
      setAcOpen(true);
    } else {
      setAcOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!acOpen) return;
    const maxIdx = (acRef.current?.itemCount ?? 7) - 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, maxIdx));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Escape') {
      setAcOpen(false);
      setActiveIndex(-1);
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      acRef.current?.selectActive();
    }
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setAcOpen(false);
        }
      }}
    >
      <form
        onSubmit={onSubmit}
        role="search"
        aria-label="Search coffee shops and roasters"
        autoComplete="off"
        noValidate
        className="w-full max-w-[680px] mx-auto"
      >
        <div className="rounded-2xl border border-[#C68642]/60 bg-card-bg overflow-hidden" style={{boxShadow: '0 0 0 1px rgba(198,132,66,0.15), 0 4px 16px rgba(0,0,0,0.4)'}}>
          {/* Input row */}
          <div className="relative">
            <input
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => inputValue.trim().length >= 2 && setAcOpen(true)}
              onKeyDown={handleKeyDown}
              placeholder="Search, Sip, Repeat"
              autoComplete="off"
              aria-label="Search by name, city, state, or ZIP"
              data-form-type="other"
              name="gr-search"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              inputMode="search"
              role="combobox"
              aria-expanded={acOpen}
              aria-autocomplete="list"
              className="w-full px-4 py-3 bg-transparent text-text-primary placeholder-text-tertiary focus:outline-none"
            />
            {inputValue && !acOpen && (
              <IconButton
                aria-label="clear search"
                onClick={handleClear}
                className="absolute right-2 top-1/2 -translate-y-1/2"
                size="small"
              >
                <X className="w-5 h-5" />
              </IconButton>
            )}
          </div>
          {/* Divider */}
          <div className="mx-3 border-t border-card-border" />
          {/* Controls row */}
          <div className="flex items-center justify-center gap-2 px-3 py-2.5">
            <Button type="submit" variant="contained" size="small">
              Search
            </Button>
            <ViewToggle size="small" />
            <SearchFilters />
          </div>
        </div>
        {/* Autocomplete outside card so it's not clipped by overflow-hidden */}
        <div className="relative">
          <SearchAutocomplete
            ref={acRef}
            inputValue={inputValue}
            onSelect={handleSelect}
            onSearchAll={handleSearchAll}
            anchorEl={inputRef.current}
            open={acOpen}
            onClose={() => setAcOpen(false)}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />
        </div>
      </form>
    </div>
  );
};

export default MobileSearchBar;
